import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { cn, useIsMobile } from '@heidi/utils';
import { Copy, CopyCheck, Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DictationBubbleShell } from '../../../shared/components/dictation-bubble-shell';
import { DictationCloseButton } from '../../../shared/components/dictation-close-button';
import { DICTATION_BUBBLE_Z_INDEX_CLASS } from '../../../shared/styles';
import { useDictationClose } from '../../hooks/useDictationClose';
import { getDictationBubbleUiHost } from '../../host';
import { useDictationStore } from '../../store/useDictationStore';
import { bubbleExitAnimation } from '../consts/animation';

export const DictationInsertionError = () => {
  const { useCopyToClipboard, useScrollGradient } = getDictationBubbleUiHost();
  const text = useDictationStore((s) => s.text);
  const { handleClose, reset } = useDictationClose();
  const { copyToClipboard } = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);
  const [shouldDismiss, setShouldDismiss] = useState(false);
  const textContentRef = useRef<HTMLDivElement>(null);
  const { showTopGradient, showBottomGradient, handleScroll } = useScrollGradient(
    textContentRef,
    [text]
  );
  const intl = useIntl();
  const isMobile = useIsMobile();

  const handleCopy = async () => {
    await copyToClipboard(text);
    setIsCopied(true);
    setShouldDismiss(true);
  };

  // Revert copy icon after 3 seconds
  useEffect(() => {
    if (!isCopied) return;

    const timeout = setTimeout(() => {
      setIsCopied(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isCopied]);

  // Dismiss the insertion error 5 seconds after copy
  useEffect(() => {
    if (!shouldDismiss) return;

    const timeout = setTimeout(() => {
      reset();
    }, 5000);

    return () => clearTimeout(timeout);
  }, [shouldDismiss, reset]);

  return (
    <DictationBubbleShell
      animation={bubbleExitAnimation}
      opaque
      className={cn(
        'fixed inset-x-0 mx-auto flex max-h-52 w-fit max-w-xl shrink-0 flex-col items-start justify-center gap-4',
        DICTATION_BUBBLE_Z_INDEX_CLASS,
        isMobile ? 'bottom-32' : 'bottom-6'
      )}
    >
      <DictationCloseButton onClose={handleClose} />
      <div className="flex flex-1 items-center gap-4">
        <div className="flex flex-1 flex-col items-start gap-1">
          <div className="flex items-center gap-1 text-xs leading-none text-text-secondary">
            <Info size={12} className="shrink-0" />
            <FormattedMessage
              defaultMessage="Click into a field before dictation to auto-fill"
              id="sAK4eISh/8"
            />
          </div>
          <div className="relative w-full">
            <div
              className={cn(
                'pointer-events-none absolute left-0 top-0 h-12 w-full bg-gradient-to-b from-surface-page/80 to-transparent transition-opacity duration-200',
                showTopGradient ? 'opacity-100' : 'opacity-0'
              )}
            />
            <div
              ref={textContentRef}
              onScroll={handleScroll}
              className="max-h-32 flex-1 self-stretch overflow-hidden overflow-y-auto whitespace-pre-line text-sm text-text-primary"
            >
              {text || (
                <FormattedMessage defaultMessage="No text available" id="APFVRytzAP" />
              )}
            </div>
            <div
              className={cn(
                'pointer-events-none absolute bottom-0 left-0 h-12 w-full bg-gradient-to-t from-surface-page/80 to-transparent transition-opacity duration-200',
                showBottomGradient ? 'opacity-100' : 'opacity-0'
              )}
            />
          </div>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                iconOnly
                size="sm"
                className="rounded-md border border-border bg-surface-page/80 text-text-primary backdrop-blur-sm"
                onClick={() => void handleCopy()}
                aria-label={intl.formatMessage({
                  defaultMessage: 'Copy to clipboard',
                  id: 'aCdAsIsVv0',
                })}
              >
                {isCopied ? <CopyCheck className="text-validation-success" /> : <Copy />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <FormattedMessage defaultMessage="Copy to clipboard" id="aCdAsIsVv0" />
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </DictationBubbleShell>
  );
};
